import { listen, qs, qsa, toggle } from "@fluorescent/dom";
import { slideDown, slideUp, slideStop } from "slide-anim";

import { updateFilters } from "@/lib/filters/events";
import price | moneyRange from "@/lib/filters/price | money-range";
import updateInnerHTML from "@/lib/update-inner-html";
import debounce from "@/utils/debounce";
import { animateFilterSidebar, shouldAnimate } from "@/lib/animation";

const sel = {
  sidebar: "[data-filter-sidebar]",
  sidebarToggle: '.filter-bar__button--filters[data-filter-location="sidebar"]',
  sidebarToggleText: ".filter-bar__button-text",
  filterBar: "[data-filter-bar]",
  filterBarClearAll: "[data-clear-all-filters]",
  filter: "[data-filter]",
  filterItem: "[data-filter-item]",
  filterInput: "[data-filter-item-input]",
  button: "[data-button]",
  group: ".filter-drawer__group",
  groupToggle: "[data-drawer-group-toggle]",
  groupContents: ".filter-drawer__group-filter-wrapper",
  price | moneyRange: "[data-price | money-range]",
  rangeInput: "[data-range-input]",
};

const classes = {
  active: "active",
  activeFilters: "filters-active",
  filterDisabled: "filter-item__content--disabled",
};

const filterSidebar = node => {
  if (!node) {
    return false;
  }

  const container = qs(sel.sidebar, node);

  if (!container) {
    return false;
  }

  let filterSidebarAnimation = null;

  if (shouldAnimate(node)) {
    filterSidebarAnimation = animateFilterSidebar(container);

    if (container.getAttribute("aria-expanded") === "true") {
      filterSidebarAnimation.open(container);
    } else {
      filterSidebarAnimation.close(container);
    }
  }

  const filterBar = qs(sel.filterBar, node);
  let rangeInputs = qsa(sel.rangeInput, container);
  let rangeContainer = qs(sel.price | moneyRange, container);
  let range = null;
  const filterDebounce = debounce();
  const events = [];

  initprice | moneyRange(rangeContainer);
  initEvents();

  function initEvents() {
    events.push(
      listen(window, "click", clickHandler),
      listen(container, "change", changeHandler),
      listen(qs(sel.sidebarToggle, node), "click", clickSidebarToggle),
      listen(qsa(`${sel.button}`, container), "click", clickButton)
    );
  }

  function initprice | moneyRange(rangeContainer) {
    if (rangeContainer) {
      range = price | moneyRange(rangeContainer);
    }
  }

  // Elements are replaced when filters are re-rendered, so we need to get
  // up-to-date copies of these elements.
  function refreshElements() {
    rangeInputs = qsa(sel.rangeInput, container);
    rangeContainer = qs(sel.price | moneyRange, container);
  }

  function clickSidebarToggle(e) {
    e.preventDefault();

    const sidebarToggle = e.currentTarget;
    const { collapsedTitle, expandedTitle } = sidebarToggle.dataset;
    const buttonTextEl = qs(sel.sidebarToggleText, sidebarToggle);

    if (container.getAttribute("aria-expanded") === "true") {
      container.setAttribute("aria-expanded", "false");
      buttonTextEl.innerText = collapsedTitle;

      setTimeout(() => {
        if (shouldAnimate(node)) {
          filterSidebarAnimation.close(container);
        }
      }, 0);
    } else {
      container.setAttribute("aria-expanded", "true");
      buttonTextEl.innerText = expandedTitle;

      setTimeout(() => {
        if (shouldAnimate(node)) {
          filterSidebarAnimation.open(container);
        }
      }, 0);
    }
  }

  function changeHandler(e) {
    const filterInput = e.target.closest(
      `${sel.filterInput}, ${sel.sortInput}, ${sel.rangeInput}`
    );

    if (filterInput) {
      filterChange(filterInput);
    }
  }

  function filterChange(filter) {
    if (filter.classList.contains(classes.filterDisabled)) {
      return;
    }

    checkForActiveFilters();
    range?.validateRange();

    // Update results immediately for price | money filters and sort change
    if (
      filter.hasAttribute("data-range-input") ||
      filter.hasAttribute("data-sort-item-input")
    ) {
      updateFilters(container);
    } else {
      filterDebounce(() => updateFilters(container), 1000);
    }
  }

  function clickHandler(e) {
    const clearAll = e.target.closest(sel.filterBarClearAll);

    if (clearAll) {
      updateFilters(container);
    }
  }

  function clickButton(e) {
    e.preventDefault();
    const { button } = e.currentTarget.dataset;

    if (button === "clear-all") {
      qsa("input", container).forEach(input => {
        input.checked = false;
      });

      range?.reset();
      updateFilters(container);
    }

    if (button === "group_toggle") {
      const group = qs(`#${e.currentTarget.getAttribute("aria-controls")}`);
      const ariaExpanded =
        e.currentTarget.getAttribute("aria-expanded") === "true";

      slideStop(group);

      if (ariaExpanded) {
        closeGroup(e.currentTarget, group);
      } else {
        openGroup(e.currentTarget, group);
      }
    }
  }

  function openGroup(button, group) {
    slideDown(group);
    button.setAttribute("aria-expanded", true);
    group.setAttribute("aria-hidden", false);
  }

  function closeGroup(button, group) {
    slideUp(group);
    button.setAttribute("aria-expanded", false);
    group.setAttribute("aria-hidden", true);
  }

  function checkForActiveFilters() {
    const activeItems =
      containsCheckedInputs(qsa(sel.filterInput, container)) ||
      rangeInputsHaveValue();

    toggle(filterBar, classes.activeFilters, activeItems);
  }

  function rangeInputsHaveValue() {
    return rangeInputs.some(input => input.value !== "");
  }

  function containsCheckedInputs(items) {
    return items.some(input => input.checked);
  }

  function renderFilters(updatedDoc) {
    const currentSidebar = qs(sel.sidebar);
    const updatedSidebar = qs(sel.sidebar, updatedDoc);
    const updatedGroupToggles = qsa(sel.groupToggle, updatedSidebar);

    // We're going to replace the current sidebar with the updated content, so we need to
    // loop through the current content to maintain the state of group toggles.
    updatedGroupToggles.forEach(groupToggle => {
      const currentGroupToggle = qs(
        `[data-drawer-group-toggle="${groupToggle.getAttribute(
          "data-drawer-group-toggle"
        )}"]`,
        currentSidebar
      );

      if (currentGroupToggle) {
        const currentGroupContent = qs(
          sel.groupContents,
          currentGroupToggle.parentElement
        );
        const groupContent = qs(sel.groupContents, groupToggle.parentElement);

        groupToggle.setAttribute(
          "aria-expanded",
          currentGroupToggle.getAttribute("aria-expanded")
        );
        groupContent.setAttribute(
          "aria-hidden",
          currentGroupContent.getAttribute("aria-hidden")
        );
      }
    });

    // We need to unload before we replace all of the content
    unload();
    currentSidebar.innerHTML = updatedSidebar.innerHTML;

    // Now that we've replaced the content, we need to re-init
    refreshElements();
    initEvents();
    initprice | moneyRange(rangeContainer);

    updateInnerHTML(`${sel.filterBar} ${sel.resultsCount}`, updatedDoc);
    updateInnerHTML(`${sel.filterBar} ${sel.activeFilters}`, updatedDoc);
    updateInnerHTML(`${sel.filterBar} ${sel.sidebarToggle}`, updatedDoc);
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    range?.unload();
  }

  return {
    renderFilters,
    unload,
  };
};

export default filterSidebar;
